import { Ionicons } from '@expo/vector-icons';
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native';

import {
  buildXLabels,
  type ChartPeriod,
  formatCurrencyAxis,
  groupByPeriod,
} from '@/components/dashboard/chart/chart-utils';
import { LineAreaChart } from '@/components/dashboard/chart/line-area-chart';
import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RECEIVABLES_EVOLUTION } from '@/content/payments';
import type { PaymentSummary } from '@/types/payment-reporting';
import { formatCurrency } from '@/utils/format-currency';
import { useThemedHex } from '@/hooks/use-theme-colors';

type Metric = 'net' | 'gross' | 'fee';

const METRICS: { id: Metric; icon: React.ComponentProps<typeof Ionicons>['name'] }[] = [
  { id: 'net', icon: 'trending-up-outline' },
  { id: 'gross', icon: 'cash-outline' },
  { id: 'fee', icon: 'pricetag-outline' },
];

const FIELD: Record<Metric, 'netAmount' | 'grossAmount' | 'feeAmount'> = {
  net: 'netAmount',
  gross: 'grossAmount',
  fee: 'feeAmount',
};

/** Quantos dias por ponto: diário até 7, a cada 2 até ~1 mês, semanal acima. */
function chartPeriodFor(spanDays: number): ChartPeriod {
  if (spanDays <= 7) return '7d';
  if (spanDays <= 31) return '30d';
  return '90d';
}

/**
 * Série densa do período: `summary.days` só traz dias com cobrança; os
 * outros entram como zero para a linha não pular datas. O meio-dia no `date`
 * evita que `buildXLabels` (que faz `new Date`) mostre o dia anterior no fuso
 * do Brasil.
 */
function denseSeries(summary: PaymentSummary, metric: Metric) {
  const start = parseISO(summary.from);
  const span = differenceInCalendarDays(parseISO(summary.to), start) + 1;
  const byDate = new Map((summary.days ?? []).map((day) => [day.date, day[FIELD[metric]]]));
  return Array.from({ length: Math.max(span, 1) }, (_, index) => {
    const key = format(addDays(start, index), 'yyyy-MM-dd');
    return { date: `${key}T12:00:00`, value: byDate.get(key) ?? 0 };
  });
}

/**
 * "Evolução" do período (mesmo card de Relatórios): chips dentro do card
 * escolhem o que a linha mostra; o total no topo é o do resumo, o mesmo
 * número do hero.
 */
export function ReceivablesEvolutionCard({ summary }: { summary: PaymentSummary }) {
  const th = useThemedHex();
  const [metric, setMetric] = useState<Metric>('net');
  const { height } = useWindowDimensions();
  const chartHeight = Math.max(180, Math.min(320, height * 0.26));
  const color = metric === 'fee' ? '#737373' : '#FD5001';

  const { values, xLabels, hasData } = useMemo(() => {
    const series = denseSeries(summary, metric);
    const grouped = groupByPeriod(series, chartPeriodFor(series.length), ['value']);
    return {
      values: grouped.map((point) => point.value),
      xLabels: buildXLabels(grouped.map((point) => point.date)),
      hasData: (summary.days ?? []).length > 0,
    };
  }, [summary, metric]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{RECEIVABLES_EVOLUTION.title}</CardTitle>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 8 }}
          className="mt-2"
        >
          {METRICS.map((item) => {
            const isSelected = metric === item.id;
            return (
              <Pressable
                key={item.id}
                onPress={() => setMetric(item.id)}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                className={`flex-row items-center gap-2 rounded-full px-4 py-2 active:opacity-80 ${
                  isSelected ? 'bg-primary' : 'bg-neutral-100'
                }`}
                testID={`receivables-evolution-${item.id}`}
              >
                <Ionicons
                  name={item.icon}
                  size={16}
                  color={isSelected ? '#FFFFFF' : th('#737373')}
                />
                <ThemedText
                  className={`font-semibold text-xs ${isSelected ? 'text-white' : 'text-neutral-600'}`}
                >
                  {RECEIVABLES_EVOLUTION.metrics[item.id]}
                </ThemedText>
              </Pressable>
            );
          })}
        </ScrollView>
        <ThemedText
          className="mt-2 font-bold text-2xl"
          style={{ color, fontVariant: ['tabular-nums'] }}
        >
          {formatCurrency(summary[FIELD[metric]])}
        </ThemedText>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <LineAreaChart
            series={[{ values, color, showArea: true, showEndDot: true }]}
            xLabels={xLabels}
            height={chartHeight}
            formatY={formatCurrencyAxis}
          />
        ) : (
          <View style={{ height: chartHeight }} className="items-center justify-center">
            <ThemedText className="text-sm text-neutral-500">
              {RECEIVABLES_EVOLUTION.empty}
            </ThemedText>
          </View>
        )}
      </CardContent>
    </Card>
  );
}
